/**
 * <cart-drawer> custom element — slide-in mini-cart panel.
 *
 * Replaces the IIFE mini-cart.js. Owns:
 *   - open / close lifecycle (fade in/out, focus trap, ESC dismiss)
 *   - delegated handling of #cart-button (open) and #close-mini-cart (close)
 *   - auto-close on internal anchor clicks (excluding `.no-ajax`)
 *   - empty-cart auto-close in response to `cart:update` (count → 0)
 *
 * Public API (called by `<add-item-form>` and `application.esm.js`):
 *   - open()    — show the drawer
 *   - close()   — hide the drawer
 *   - isOpen    — boolean getter
 *
 * Dispatches `cart-drawer:open` / `cart-drawer:close` native CustomEvents
 * on the `document` so `application.esm.js`'s no-click overlay can show
 * / hide the veil without coupling through `app.set('isMiniCartOpen', ...)`.
 *
 * Source path: theme/js/components/cart-drawer.esm.js
 * Built name : ./cart-drawer.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Opus 4.7
 *
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { trapFocusOn } from './dom.js';
import { push as notifierPush } from './notifier.js';
import { fadeIn, fadeOut, stop } from './core-anim.js';
import { getCurrentCart } from './cart-state.js';
import { saveLastFocused, restoreLastFocused, bindEscape, unbindEscape } from './overlay.js';

const CART_BUTTON_SELECTOR = '#cart-button';
const CLOSE_BUTTON_SELECTOR = '#close-mini-cart';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._open = false;
    this._onDocClick = this._onDocClick.bind(this);
    this._onCartUpdate = this._onCartUpdate.bind(this);
  }

  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('cart:update', this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  get isOpen() {
    return this._open;
  }

  open() {
    if (this._open) return;
    log('[MiniCart] opened');
    const animationProps = appGet('animations.modal.in', { duration: 125 });
    saveLastFocused();
    this._open = true;
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    stop(this);
    fadeIn(this, { duration: animationProps.duration, display: 'block' }).then(() => {
      setTimeout(() => {
        trapFocusOn(this);
        const closeBtn = this.querySelector(CLOSE_BUTTON_SELECTOR);
        if (closeBtn) closeBtn.focus();
        bindEscape(this, () => this.close());
      }, 50);
    });
  }

  close() {
    if (!this._open) return;
    const animationProps = appGet('animations.modal.out', { duration: 125 });
    this._open = false;
    log('[MiniCart] closed');
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
    unbindEscape(this);
    restoreLastFocused(50);
    stop(this);
    fadeOut(this, { duration: animationProps.duration });
    safeAppCall('ga', 'send', 'event', {
      eventCategory: 'MiniCart',
      eventAction: 'Closed',
    });
  }

  _onDocClick(event) {
    const target = event.target;
    const cartBtn = target.closest(CART_BUTTON_SELECTOR);
    if (cartBtn) {
      event.preventDefault();
      this._handleCartButtonClick();
      return;
    }
    const closeBtn = target.closest(CLOSE_BUTTON_SELECTOR);
    if (closeBtn) {
      event.preventDefault();
      if (this._open) this.close();
      return;
    }
    // Auto-close on link clicks within the drawer (excluding .no-ajax,
    // which the legacy mini-cart used to skip remove-line-item links).
    if (this._open && this.contains(target)) {
      const link = target.closest('a:not(.no-ajax)');
      if (link && this.contains(link)) {
        this.close();
      }
    }
  }

  _handleCartButtonClick() {
    if (appGet('url.path') === '/cart') return;
    if (this._open) return;
    const cart = getCurrentCart();
    if (cart && cart.normalized && cart.normalized.item_count > 0) {
      this.open();
      safeAppCall('ga', 'send', 'event', {
        eventCategory: 'MiniCart',
        eventAction: 'Opened - Cart Button Clicked',
      });
    } else {
      notifierPush(appGet('notices.cart.empty'));
      safeAppCall('ga', 'send', 'event', {
        eventCategory: 'FlashMessage',
        eventAction: 'Shown',
        eventLabel: 'Cart empty',
      });
    }
  }

  _onCartUpdate(event) {
    const cart = event && event.detail && event.detail.cart;
    const count = cart && cart.normalized ? cart.normalized.item_count : null;
    if (count === 0 && this._open) {
      this.close();
      notifierPush(appGet('notices.cart.empty'));
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

log('[MiniCart] <cart-drawer> registered');
